import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { archiveService, ArchiveType } from '../services/archive.service';
import { ArchiveItem } from '../types';
import { ArchiveTable, ArchiveToolbar } from '../components/archive/ArchiveTable';
import { ArchiveForm } from '../components/archive/ArchiveForm';

export default function ArchivePage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ArchiveType | ''>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ArchiveItem | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: archiveData, isLoading } = useQuery({
    queryKey: ['archive', { page, limit, search, typeFilter }],
    queryFn: () => {
      const filters: Record<string, any> = { page, limit, search };
      if (typeFilter) filters.type = typeFilter;
      return archiveService.getList(filters);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<ArchiveItem, 'id' | 'createdAt' | 'updatedAt'>) => archiveService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Запись добавлена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ArchiveItem> }) => archiveService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Запись обновлена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => archiveService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive'] });
      setNotification({ message: 'Запись удалена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const togglePublicMutation = useMutation({
    mutationFn: (id: number) => archiveService.togglePublic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive'] });
      setNotification({ message: 'Статус изменен', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка', type: 'error' }),
  });

  const filteredItems = archiveData?.data || [];
  const total = archiveData?.pagination?.total || 0;

  const handleCreate = () => { setEditingItem(null); setIsFormOpen(true); };
  const handleEdit = (item: ArchiveItem) => { setEditingItem(item); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    if (editingItem) await updateMutation.mutateAsync({ id: editingItem.id, data });
    else await createMutation.mutateAsync(data);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить запись?')) deleteMutation.mutate(id); };
  const handleTogglePublic = (id: number) => togglePublicMutation.mutate(id);
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Архив</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить запись</Button>
      </Box>
      <ArchiveToolbar search={search} onSearchChange={(value) => { setSearch(value); setPage(1); }} typeFilter={typeFilter} onTypeFilterChange={(value) => { setTypeFilter(value as ArchiveType | ''); setPage(1); }} />
      <ArchiveTable items={filteredItems} loading={isLoading} total={total} page={page} limit={limit} onPageChange={setPage} onEdit={handleEdit} onDelete={handleDelete} onTogglePublic={handleTogglePublic} />
      <ArchiveForm open={isFormOpen} onClose={() => setIsFormOpen(false)} onSubmit={handleSubmit} initialData={editingItem as any} loading={createMutation.isPending || updateMutation.isPending} error={createMutation.error?.message || updateMutation.error?.message} />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
